import Phaser from 'phaser';
import { STRIDE_X, STRIDE_Y, landTiles, type MaskJson } from '../data/australiaMap';
import { INVENTIONS } from '../data/inventions';
import {
  loadScore, elapsedMs, formatElapsed, winCount, solvedCount,
} from '../state/scoreboard';
import { BIG_THINGS } from '../data/bigThings';
import { unlockedIds, newlyEarned, cycleProgress } from '../state/milestones';

const REVEALED_KEY = 'pa-revealed';
const STATUS_KEY = 'pa-revealed-status'; // { id: 'win' | 'gave-up' }
const INTRO_SEEN_KEY = 'pa-intro-seen';

function loadRevealedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(REVEALED_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {}
  return new Set();
}

type RevealStatus = 'win' | 'gave-up';
function loadRevealedStatus(): Record<string, RevealStatus> {
  try {
    const raw = localStorage.getItem(STATUS_KEY);
    if (raw) return JSON.parse(raw) as Record<string, RevealStatus>;
  } catch {}
  return {};
}

export class ContinentScene extends Phaser.Scene {
  private allTiles: Phaser.GameObjects.Image[] = [];
  private offsetX = 0;
  private offsetY = 0;
  private mapScale = 1;
  // Land bounding box in mask grid coordinates — saved so the geographic
  // projection used by Big Thing placement can reference the same bounds.
  private landMinCol = 0;
  private landMaxCol = 0;
  private landMinRow = 0;
  private landMaxRow = 0;
  // Each invention "owns" a Voronoi blotch of land tiles. As the player
  // solves cards, those tiles flip to green so the map fills in over time.
  private cardTileMap: Map<string, Phaser.GameObjects.Image[]> = new Map();
  // Chrome elements we hide while the riddle modal is open so they don't
  // bleed through the translucent backdrop.
  private chromeObjects: Phaser.GameObjects.GameObject[] = [];

  // ---- HUD state -------------------------------------------------------
  // Live readout in the top-right corner: solved/total, time elapsed, and
  // a "next monument unlocks at N" hint. Ticks once per second.
  private hudTime?: Phaser.GameObjects.Text;
  private hudMonuments?: Phaser.GameObjects.Text;
  private hudTimer?: Phaser.Time.TimerEvent;
  // The Cabinet button (gold pill).
  private stashBtn?: Phaser.GameObjects.Text;
  // Mobile-only segmented progress bar under the Cabinet button.
  private mobileProgressBar?: Phaser.GameObjects.Container;
  // Mobile-only mantelpiece showing unlocked monuments. Replaces the CTA
  // once the first monument is earned — instructions fade, trophies appear.
  private mobileMantelpiece?: Phaser.GameObjects.Container;
  private mobileCta?: Phaser.GameObjects.Text;

  // ---- Big Things state -------------------------------------------------
  // Five iconic Australian roadside-attraction monuments dotted across the
  // map. They start as dim silhouettes from frame one, then resolve to
  // full colour as the player hits milestone solves.
  private bigThingSprites: Map<string, Phaser.GameObjects.Image> = new Map();

  constructor() {
    super('Continent');
  }

  create() {
    const mask: MaskJson | undefined = this.cache.json.get('mask');
    if (!mask) {
      this.add.text(20, 20, 'Missing data/australia-mask.json — run `npm run bake:mask`.', {
        fontFamily: 'monospace', fontSize: '12px', color: '#ff6464',
      });
      return;
    }

    // Centre the map within the game canvas. Compute the actual bbox of
    // *land* cells (the SVG bbox includes ocean padding on the west, which
    // would otherwise push the rendered continent off-centre).
    let minCol = Infinity, maxCol = -Infinity, minRow = Infinity, maxRow = -Infinity;
    for (const { col, row } of landTiles(mask)) {
      if (col < minCol) minCol = col;
      if (col > maxCol) maxCol = col;
      if (row < minRow) minRow = row;
      if (row > maxRow) maxRow = row;
    }
    const landCols = maxCol - minCol + 1;
    const landRows = maxRow - minRow + 1;
    const canvasW = Number(this.game.config.width);
    const canvasH = Number(this.game.config.height);
    // Scale the whole map down on narrow (portrait-mobile) canvases so it
    // fits without overflowing horizontally and leaves room for the title +
    // CTA padding. On wide canvases we render at native scale.
    const mapScale = canvasW < 1100 ? 0.5 : 1.0;
    this.mapScale = mapScale;
    const sx = STRIDE_X * mapScale;
    const sy = STRIDE_Y * mapScale;
    const totalW = landCols * sx;
    const totalH = landRows * sy;
    const offsetX = Math.floor((canvasW - totalW) / 2) - minCol * sx;
    // Top reserve for header chrome (title + subline + HUD column on desktop,
    // or the centred Cabinet button on narrow). Bottom reserve covers the
    // CTA and, on mobile, the mantelpiece shelf showing unlocked monuments.
    const headerReserve = canvasW < 1100 ? 220 : 200;
    const footerReserve = canvasW < 1100 ? 170 : 80;
    const mapAreaH = canvasH - headerReserve - footerReserve;
    const offsetY = headerReserve + Math.floor((mapAreaH - totalH) / 2) - minRow * sy;
    this.landMinCol = minCol;
    this.landMaxCol = maxCol;
    this.landMinRow = minRow;
    this.landMaxRow = maxRow;

    this.offsetX = offsetX;
    this.offsetY = offsetY;
    (window as any).__continent = this; // debug handle

    // Tiles — every cell renders the WA red-dirt sprite for a unified
    // continent palette. (Earlier iterations supported per-state tilesets
    // and a skin-switcher; that's been removed since the player feedback
    // was that the multi-colour map didn't read as well.)
    for (const { col, row } of landTiles(mask)) {
      const x = offsetX + col * sx + sx / 2;
      const y = offsetY + row * sy + sy / 2;
      const sprite = this.add.image(x, y, 'tile-wa');
      sprite.setScale(mapScale);
      sprite.setDepth(row * 100 + col);
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerover', () => this.handleHover());
      sprite.on('pointerout', () => this.clearHover());
      sprite.on('pointerup', () => this.onMapClicked());
      this.allTiles.push(sprite);
    }

    // Compute per-card tile chunks now that all tiles are positioned, then
    // paint any already-resolved cards' chunks with their status colour.
    this.computeCardTileMap();
    this.applyRevealedStatusToTiles(/*animate*/ false);

    // The Big Things live in a dedicated legend panel under the HUD now,
    // not on the map. The 5 real-world monuments cluster on the east coast
    // so a geographic plot looked like one blob. A panel reads better.
    // (See buildMonumentsLegend, called from HUD setup.)

    // Title — narrow canvases (portrait phones) need oversized native sizes
    // because FIT scales them down by ~50% on a 430px viewport.
    const narrow = canvasW < 1100;
    const title = this.add
      .text(canvasW / 2, narrow ? 60 : 32, 'Auscyclopedia', {
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: narrow ? '64px' : '46px',
        color: '#f2e5c0',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);

    // Subline.
    const subline = this.add
      .text(canvasW / 2, narrow ? 110 : 80, 'things built here', {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: narrow ? '24px' : '17px',
        color: '#b6a079',
      })
      .setOrigin(0.5);

    // Pulsing call-to-action. On narrow it sits at the same y as the
    // mantelpiece — the CTA hides once the first monument unlocks and
    // the trophy row takes over the same slot.
    const ctaLabel = narrow
      ? '▸  tap the map for a riddle  ◂'
      : '▸  click the map for a riddle  ◂';
    const ctaY = narrow ? canvasH - 90 : canvasH - 38;
    const cta = this.add
      .text(canvasW / 2, ctaY, ctaLabel, {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: narrow ? '24px' : '19px',
        color: '#ffd966',
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: cta,
      alpha: { from: 1, to: 0.4 },
      duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.InOut',
    });
    if (narrow) this.mobileCta = cta;

    // "The Cabinet" — opens the read-only collection of unlocked cards.
    // On narrow (mobile) it sits centred under the subline AND embeds the
    // running count so every solve gives visible progress feedback (the
    // mobile home page has no top-right HUD). On wide it sits top-left.
    const stashX = narrow ? canvasW / 2 : 30;
    const stashY = narrow ? 160 : 36;
    const stashOriginX = narrow ? 0.5 : 0;
    const stashLabelSize = narrow ? '24px' : '19px';
    const stashBtn = this.add
      .text(stashX, stashY, this.cabinetButtonLabel(narrow), {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: stashLabelSize,
        color: '#0d0a08',
        backgroundColor: '#ffd966',
        padding: { x: narrow ? 16 : 12, y: narrow ? 10 : 6 },
      })
      .setOrigin(stashOriginX, 0.5)
      .setInteractive({ useHandCursor: true });
    stashBtn.on('pointerover', () => stashBtn.setBackgroundColor('#fff5c0'));
    stashBtn.on('pointerout', () => stashBtn.setBackgroundColor('#ffd966'));
    stashBtn.on('pointerup', () => this.openPokedex());
    this.stashBtn = stashBtn;

    // Mobile progress bar — five segmented chunks under the Cabinet button.
    // Each cell fills with gold as you solve, a mystery gift box sits at
    // the end. Pure visual goal, no counter text.
    if (narrow) {
      this.mobileProgressBar = this.add.container(canvasW / 2, stashY + 70);
      this.refreshMobileProgressBar();

      // Mantelpiece — sits at the same y as the CTA. Hidden until the
      // first monument unlocks; then the CTA hides and trophies appear.
      this.mobileMantelpiece = this.add.container(canvasW / 2, canvasH - 90);
      this.refreshMobileMantelpiece();
    }

    // ---- Live HUD top-right -----------------------------------------
    // Tracks unlocked count, guesses used, time elapsed, and which beer
    // tier the player is on track for. Ticks once per second.
    const hudObjects = this.buildHud();

    // Track chrome objects so we can hide them when modals are open.
    this.chromeObjects = [title, subline, cta, stashBtn, ...hudObjects];
    if (this.mobileProgressBar) this.chromeObjects.push(this.mobileProgressBar);
    if (this.mobileMantelpiece) this.chromeObjects.push(this.mobileMantelpiece);

    // Show the intro / lay-of-the-land card on first visit.
    let introSeen = false;
    try { introSeen = !!localStorage.getItem(INTRO_SEEN_KEY); } catch {}
    if (!introSeen) {
      // Defer one tick so all chrome and tiles are mounted first.
      this.time.delayedCall(0, () => this.showIntroCard());
    }
  }

  // ---------------------------------------------------------- HUD
  private buildHud(): Phaser.GameObjects.GameObject[] {
    const W = Number(this.game.config.width);
    const narrow = W < 1100;

    // On narrow (mobile) the home-page HUD is hidden — the run stats and
    // monuments row live inside The Cabinet page instead, where they can
    // breathe without crowding the map.
    if (narrow) return [];

    const rightX = W - 30;
    const topY = 36;

    // Just the run timer + the monuments row. The "X/24 unearthed" count
    // used to live here but it doesn't carry meaning on its own — the only
    // progression that matters is unlocking the monuments.
    this.hudTime = this.add.text(rightX, topY, '', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '16px', color: '#b6a079',
    }).setOrigin(1, 0);

    const legendObjects = this.buildMonumentsLegend(
      rightX, topY + 30, /*centered*/ false,
    );

    this.updateHud();
    this.hudTimer = this.time.addEvent({
      delay: 1000, loop: true, callback: () => this.updateHud(),
    });

    return [this.hudTime, ...legendObjects];
  }

  /**
   * Build the monuments legend — a quiet inline row of icons, no casing.
   * Locked icons render as ghosted silhouettes. Reveals on milestone unlocks.
   *
   * `anchorX` is right-edge X on wide layouts and centre X on narrow.
   */
  private buildMonumentsLegend(
    anchorX: number,
    topY: number,
    centered: boolean,
  ): Phaser.GameObjects.GameObject[] {
    const W = Number(this.game.config.width);
    const narrow = W < 1100;
    const objects: Phaser.GameObjects.GameObject[] = [];

    const iconH = narrow ? 36 : 40;
    const iconGap = narrow ? 8 : 8;

    // Compute the effective row width using each icon's actual native size
    // so the anchor maths works regardless of texture sizes.
    const widths = BIG_THINGS.map((bt) => {
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      const nativeH = tex?.source?.[0]?.height ?? iconH;
      const nativeW = tex?.source?.[0]?.width ?? iconH;
      return (nativeW * iconH) / nativeH;
    });
    const totalW = widths.reduce((a, b) => a + b, 0) + iconGap * (BIG_THINGS.length - 1);

    // Single inline label "monuments  0/5" sitting just above the row.
    this.hudMonuments = this.add.text(anchorX, topY, '', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '13px' : '15px',
      color: '#b19b6e',
    }).setOrigin(centered ? 0.5 : 1, 0);
    objects.push(this.hudMonuments);

    const rowY = topY + (narrow ? 24 : 20) + iconH / 2;
    const rowLeft = centered ? anchorX - totalW / 2 : anchorX - totalW;
    const unlocked = unlockedIds();
    let cursorX = rowLeft;
    BIG_THINGS.forEach((bt, i) => {
      const w = widths[i];
      const cx = cursorX + w / 2;
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      const nativeH = tex?.source?.[0]?.height ?? iconH;
      const scale = nativeH ? iconH / nativeH : 1;

      const icon = this.add.image(cx, rowY, key)
        .setOrigin(0.5)
        .setScale(scale);
      objects.push(icon);
      this.bigThingSprites.set(bt.id, icon);

      const isUnlocked = unlocked.has(bt.id);
      if (isUnlocked) {
        icon.setTint(0xffffff).setAlpha(1);
        // Clicking an unlocked monument re-opens its info card in replay mode.
        icon.setInteractive({ useHandCursor: true });
        icon.on('pointerover', () => icon.setScale(scale * 1.1));
        icon.on('pointerout', () => icon.setScale(scale));
        icon.on('pointerup', () => {
          this.scene.launch('Milestone', { bigThingId: bt.id, replay: true });
          this.scene.bringToTop('Milestone');
        });
      } else {
        // Locked — earth-toned silhouette so the shape just barely reads.
        icon.setTint(0x3a2a1a).setAlpha(0.6);
      }

      cursorX += w + iconGap;
    });

    return objects;
  }

  private updateHud() {
    if (!this.hudTime && !this.hudMonuments) return;
    const score = loadScore();
    const time = formatElapsed(elapsedMs(score));
    const unlocked = unlockedIds().size;

    this.hudTime?.setText(`time  ${time}`);
    this.hudMonuments?.setText(`unlocked  ${unlocked} / ${BIG_THINGS.length}`);
  }

  // ---------------------------------------------------------- intro card
  private showIntroCard() {
    const W = Number(this.game.config.width);
    const H = Number(this.game.config.height);
    const narrow = W < 1100;

    // Hide chrome behind the modal so the tier ladder doesn't fight it.
    this.setChromeVisible(false);

    // Tiles set their depth to row*100+col, which can reach ~8000.
    // Modal layer must beat that to sit on top.
    const layer = this.add.container(0, 0).setDepth(100000);

    // Backdrop — opaque so the map silhouette fades out behind the card.
    const back = this.add.rectangle(W / 2, H / 2, W, H, 0x0a0806, 0.94)
      .setInteractive();
    layer.add(back);

    const cardCx = W / 2;
    const cardCy = H / 2;
    const cardW = Math.min(W - (narrow ? 32 : 80), narrow ? 720 : 560);
    // Tight padding budget — the card sizes itself to its content.
    const padTop = narrow ? 48 : 36;
    const padBottom = narrow ? 40 : 30;

    // Build content top-down measuring as we go, then frame underneath.
    let cy = 0; // we'll offset everything by the final card top later
    const contentItems: Phaser.GameObjects.GameObject[] = [];

    // Australia emblem — chunky 8-bit silhouette at the top of the card,
    // acts as the brand mark. Falls back to the smoother variant if the
    // chunky one hasn't been baked yet.
    const emblemKey = this.textures.exists('australia-chunky')
      && this.textures.get('australia-chunky').source[0]?.width > 1
      ? 'australia-chunky'
      : (this.textures.exists('australia')
          && this.textures.get('australia').source[0]?.width > 1
          ? 'australia' : null);
    if (emblemKey) {
      const emblemH = narrow ? 100 : 76;
      const tex = this.textures.get(emblemKey);
      const nativeH = tex.source[0].height;
      const emblem = this.add.image(cardCx, cy + emblemH / 2, emblemKey)
        .setOrigin(0.5)
        .setScale(emblemH / nativeH);
      contentItems.push(emblem);
      cy += emblemH + (narrow ? 18 : 14);
    }

    // Headline — direct objective
    const headline = this.add.text(cardCx, cy, "Unlock Australia's landmarks", {
      fontFamily: 'Fraunces, Georgia, serif',
      fontSize: narrow ? '38px' : '40px',
      color: '#f2e5c0', fontStyle: 'italic',
      align: 'center',
      wordWrap: { width: cardW - (narrow ? 40 : 60) },
      lineSpacing: 4,
    }).setOrigin(0.5, 0);
    contentItems.push(headline);
    cy += headline.height + (narrow ? 24 : 18);

    // Sub — how
    const sub = this.add.text(cardCx, cy,
      'Solve invention riddles to reveal the five hidden monuments.', {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: narrow ? '22px' : '17px',
        color: '#b19b6e',
        align: 'center',
        wordWrap: { width: cardW - (narrow ? 40 : 60) },
        lineSpacing: 4,
      }).setOrigin(0.5, 0);
    contentItems.push(sub);
    cy += sub.height + (narrow ? 30 : 24);

    // Five monument silhouettes — front and centre, prizes lead.
    const slotW = (cardW - (narrow ? 80 : 60)) / BIG_THINGS.length;
    const slotIconH = narrow ? 56 : 44;
    const rowY = cy;
    const iconObjects: Phaser.GameObjects.GameObject[] = [];
    BIG_THINGS.forEach((bt, i) => {
      const slotX = cardCx - cardW / 2 + (narrow ? 40 : 30) + slotW * (i + 0.5);
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      const nativeH = tex?.source?.[0]?.height ?? slotIconH;
      const scale = nativeH ? slotIconH / nativeH : 1;
      const icon = this.add.image(slotX, rowY + slotIconH / 2, key)
        .setOrigin(0.5)
        .setScale(scale)
        .setTint(0x4a3e2a)        // warm earthy silhouette
        .setAlpha(0.75);          // visible enough to read on phones in daylight
      iconObjects.push(icon);
      const label = this.add.text(slotX, rowY + slotIconH + (narrow ? 6 : 4), '?', {
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: narrow ? '24px' : '20px',
        color: '#b19b6e', fontStyle: 'italic',   // brighter amber so it reads
      }).setOrigin(0.5, 0);
      iconObjects.push(label);
    });
    // Generous gap between the silhouette row and the CTA so the
    // monuments preview gets to breathe before the action button.
    cy += slotIconH + (narrow ? 64 : 52);

    const cta = this.add.text(cardCx, cy, '▸  start solving  ▸', {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: narrow ? '20px' : '21px',
      color: '#0d0a08', backgroundColor: '#ffd966',
      padding: { x: narrow ? 26 : 26, y: narrow ? 12 : 12 },
    }).setOrigin(0.5, 0).setInteractive({ useHandCursor: true });
    cta.on('pointerover', () => cta.setBackgroundColor('#fff5c0'));
    cta.on('pointerout', () => cta.setBackgroundColor('#ffd966'));
    cta.on('pointerup', () => {
      try { localStorage.setItem(INTRO_SEEN_KEY, '1'); } catch {}
      this.tweens.add({
        targets: layer, alpha: 0, duration: 200, ease: 'Sine.Out',
        onComplete: () => {
          layer.destroy();
          this.setChromeVisible(true);
        },
      });
    });
    cy += (cta.height || (narrow ? 50 : 40));

    // Now we know the natural content height. Build the frame to fit.
    const contentH = cy;
    const cardH = contentH + padTop + padBottom;
    const cardTop = cardCy - cardH / 2;

    // Card frame (added BEFORE content visually but added to layer first
    // so it sits underneath). Card fill is a warm dark brown (not near-black)
    // so phone screens in daylight don't lose all contrast inside the frame.
    const frameOuter = this.add.rectangle(cardCx, cardCy, cardW + 8, cardH + 8, 0x6e5a3a)
      .setStrokeStyle(2, 0xc8b18a);
    const frame = this.add.rectangle(cardCx, cardCy, cardW, cardH, 0x2a221c)
      .setStrokeStyle(1, 0xc8b18a);
    layer.add([frameOuter, frame]);

    // Shift all content down so it sits inside the framed card.
    const yShift = cardTop + padTop;
    for (const obj of contentItems) {
      (obj as Phaser.GameObjects.Components.Transform).y += yShift;
      layer.add(obj);
    }
    for (const obj of iconObjects) {
      (obj as Phaser.GameObjects.Components.Transform).y += yShift;
      layer.add(obj);
    }
    cta.y += yShift;
    layer.add(cta);
  }

  /** Cabinet button label — same on mobile and desktop. */
  private cabinetButtonLabel(_narrow: boolean): string {
    return '▸ Shelf';
  }

  /**
   * Render / re-render the mobile-only segmented progress bar showing
   * cycle progress toward the next monument. Each solve fills one cell.
   * Trailing icon is a dim silhouette of the upcoming monument so the
   * goal is visible at a glance.
   */
  private refreshMobileProgressBar() {
    const c = this.mobileProgressBar;
    if (!c) return;
    c.removeAll(true);

    const score = loadScore();
    const { filled, total, nextMonument } = cycleProgress(winCount(score));

    if (!nextMonument) {
      // All monuments earned — show a celebratory single line, no bar.
      c.add(this.add.text(0, 0, '✦  ALL MONUMENTS UNEARTHED  ✦', {
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '20px', color: '#ffd966',
      }).setOrigin(0.5));
      return;
    }

    // Cell sizing — chunky pixel-art rectangles. Trail icon (gift box)
    // is sized noticeably bigger than the cells so it reads as the prize
    // you're working toward.
    const cellW = 36;
    const cellH = 22;
    const cellGap = 6;
    const iconGap = 24;
    const trailIconH = 88;
    const barW = total * cellW + (total - 1) * cellGap;
    const trailIconW = trailIconH;

    // Total width: bar + gap + trail icon
    const fullW = barW + iconGap + trailIconW;
    const startX = -fullW / 2;

    // Cells
    for (let i = 0; i < total; i++) {
      const cx = startX + i * (cellW + cellGap) + cellW / 2;
      const isFilled = i < filled;
      // Outline
      const cell = this.add.rectangle(cx, 0, cellW, cellH,
        isFilled ? 0xffd966 : 0x1a1714)
        .setStrokeStyle(2, isFilled ? 0xc8a042 : 0x4a3e2a);
      c.add(cell);
      // Inner highlight on filled cells (gives the "earned" pixel-art feel)
      if (isFilled) {
        c.add(this.add.rectangle(cx, -3, cellW - 8, 3, 0xfff5c0));
      }
    }

    // Trailing mystery box — purely a "what's coming" tease, no spoilers
    // about which specific monument. Full colour, tilts gently to draw
    // the eye.
    const trailX = startX + barW + iconGap + trailIconW / 2;
    const key = 'mystery-box';
    const tex = this.textures.exists(key) ? this.textures.get(key) : null;
    const nativeH = tex?.source?.[0]?.height ?? trailIconH;
    const validTexture = tex && tex.source?.[0]?.width > 1;
    if (validTexture) {
      const scale = nativeH ? trailIconH / nativeH : 1;
      const icon = this.add.image(trailX, 0, key).setOrigin(0.5).setScale(scale);
      c.add(icon);
      this.tweens.add({
        targets: icon, angle: { from: -6, to: 6 },
        duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.InOut',
      });
    } else {
      // Fallback while the PNG hasn't been baked yet — gold "?" glyph.
      c.add(this.add.text(trailX, 0, '?', {
        fontFamily: 'Fraunces, Georgia, serif',
        fontSize: '32px', color: '#ffd966', fontStyle: 'italic',
      }).setOrigin(0.5));
    }
  }

  /**
   * Mobile mantelpiece — a centred row of unlocked monuments at the
   * bottom of the screen. Hidden until the first monument is earned;
   * once it appears, the "tap the map for a riddle" CTA hides itself
   * because the trophies make the affordance obvious.
   */
  private refreshMobileMantelpiece() {
    const c = this.mobileMantelpiece;
    if (!c) return;
    c.removeAll(true);

    const unlocked = unlockedIds();
    const earned = BIG_THINGS.filter((bt) => unlocked.has(bt.id));

    if (earned.length === 0) {
      // Show the CTA, hide the mantelpiece (no trophies yet).
      this.mobileCta?.setVisible(true);
      return;
    }

    // We have trophies — hide the CTA, render the row centred.
    this.mobileCta?.setVisible(false);

    const iconH = 80;
    const slotW = 100;
    const totalW = earned.length * slotW;
    const startX = -totalW / 2 + slotW / 2;

    earned.forEach((bt, i) => {
      const cx = startX + i * slotW;
      const key = `bigthing-${bt.id}`;
      const tex = this.textures.exists(key) ? this.textures.get(key) : null;
      if (!tex || !(tex.source?.[0]?.width > 1)) return;
      const nativeH = tex.source[0].height;
      const scale = nativeH ? iconH / nativeH : 1;
      const icon = this.add.image(cx, 0, key)
        .setOrigin(0.5)
        .setScale(scale);
      c.add(icon);
      icon.setInteractive({ useHandCursor: true });
      icon.on('pointerover', () => icon.setScale(scale * 1.1));
      icon.on('pointerout', () => icon.setScale(scale));
      icon.on('pointerup', () => {
        this.scene.launch('Milestone', { bigThingId: bt.id, replay: true });
        this.scene.bringToTop('Milestone');
      });
    });
  }

  /** Show / hide all home-page chrome (title, subline, CTA, stash button). */
  public setChromeVisible(visible: boolean) {
    for (const obj of this.chromeObjects) {
      (obj as Phaser.GameObjects.Text).setVisible(visible);
    }
    // When restoring chrome after a modal closes, re-apply the CTA /
    // mantelpiece toggle so the CTA stays hidden if monuments are already
    // unlocked (the mantelpiece owns that slot).
    if (visible) this.refreshMobileMantelpiece();
  }

  /**
   * Map-click handler. If there are still unrevealed cards, pick one at
   * random and launch the riddle scene. Otherwise (collection complete)
   * fall through to opening the stash.
   */
  private onMapClicked() {
    const revealed = loadRevealedIds();
    const unrevealed = INVENTIONS.filter((inv) => !revealed.has(inv.id));
    // Game complete — tapping the map re-opens the ceremony so the player
    // can revisit their result and share it. The Cabinet button still
    // gets you the read-only collection.
    if (unrevealed.length === 0) {
      this.scene.launch('Ceremony');
      this.scene.bringToTop('Ceremony');
      return;
    }

    // Preferred opening order — start with three iconic Aussie things
    // anyone will recognise (goon, tim tam, ute). Hook the player before
    // throwing the more obscure cards at them.
    const PREFERRED_OPENERS = ['wine-cask', 'tim-tam', 'ute'];
    let pick = unrevealed[0];
    for (const id of PREFERRED_OPENERS) {
      const found = unrevealed.find((inv) => inv.id === id);
      if (found) { pick = found; break; }
      // none of the preferred openers left — fall through to random.
      if (PREFERRED_OPENERS.indexOf(id) === PREFERRED_OPENERS.length - 1) {
        pick = unrevealed[Math.floor(Math.random() * unrevealed.length)];
      }
    }

    this.scene.launch('Riddle', { inventionId: pick.id });
    this.scene.bringToTop('Riddle');
  }

  private openPokedex() {
    this.scene.launch('Pokedex');
    this.scene.bringToTop('Pokedex');
  }

  // Called by RiddleScene after a riddle is solved or given up. Repaints
  // the resolved card's chunk of tiles with a brief flash animation, then
  // checks whether a milestone Big Thing was just earned, and finally
  // launches the ceremony if the full collection is complete.
  public onRiddleResolved(inventionId?: string) {
    this.applyRevealedStatusToTiles(/*animate*/ true, inventionId);
    this.updateHud();
    // Refresh the mobile progress bar so the next cell fills in.
    this.refreshMobileProgressBar();
    this.refreshMobileMantelpiece();
    const score = loadScore();
    // Monument progress is keyed off WINS only — revealed-via-give-up
    // cards fill the cabinet but don't earn monument ticks.
    const wins = winCount(score);

    // Milestone reveal — fire one popup at a time. If the player somehow
    // crossed two milestones in one solve we'll only show the most recent;
    // the rest will resolve quietly on the map.
    const earned = newlyEarned(wins);
    if (earned.length > 0) {
      // Let the green-fill animation play first.
      this.time.delayedCall(900, () => {
        this.scene.launch('Milestone', { bigThingId: earned[0].id });
        this.scene.bringToTop('Milestone');
        // Quietly mark the others as unlocked too — they animate when the
        // user closes the popup.
        for (let i = 1; i < earned.length; i++) {
          this.unlockBigThingSilently(earned[i].id);
        }
      });
      return;
    }

    // End-of-game trigger: all 24 cards have been used (won OR revealed).
    // Whether or not all monuments unlocked, the player needs a finish
    // screen with their result and a share option.
    if (solvedCount(score) >= INVENTIONS.length) {
      this.time.delayedCall(900, () => {
        this.scene.launch('Ceremony');
        this.scene.bringToTop('Ceremony');
      });
    }
  }

  /** Called by MilestoneScene when the popup closes — lights up the silhouette. */
  public onMilestoneClosed(bigThingId: string) {
    this.unlockBigThing(bigThingId);
    this.updateHud();
    // Refresh the mobile mantelpiece so the new monument appears on the shelf.
    this.refreshMobileMantelpiece();
    // If this milestone was the last card the player had left, fire the
    // ceremony (covers the case where the final card is a win).
    const score = loadScore();
    if (solvedCount(score) >= INVENTIONS.length) {
      this.time.delayedCall(700, () => {
        this.scene.launch('Ceremony');
        this.scene.bringToTop('Ceremony');
      });
    }
  }

  /** Animate a locked Big Thing icon (in the legend panel) into full colour. */
  private unlockBigThing(id: string) {
    const sprite = this.bigThingSprites.get(id);
    if (!sprite) return;
    sprite.clearTint();
    sprite.setAlpha(1);
    const finalScale = sprite.scale;
    this.tweens.add({
      targets: sprite, scale: { from: finalScale * 1.4, to: finalScale },
      duration: 600, ease: 'Back.Out',
    });
    this.makeIconClickable(id, sprite, finalScale);
  }

  /** Reveal without the splashy animation — for "I crossed two milestones" cases. */
  private unlockBigThingSilently(id: string) {
    const sprite = this.bigThingSprites.get(id);
    if (!sprite) return;
    sprite.clearTint();
    sprite.setAlpha(1);
    this.makeIconClickable(id, sprite, sprite.scale);
  }

  /** Wire pointer events so clicking the icon re-opens its info modal. */
  private makeIconClickable(id: string, sprite: Phaser.GameObjects.Image, baseScale: number) {
    sprite.setInteractive({ useHandCursor: true });
    sprite.removeAllListeners();
    sprite.on('pointerover', () => sprite.setScale(baseScale * 1.1));
    sprite.on('pointerout', () => sprite.setScale(baseScale));
    sprite.on('pointerup', () => {
      this.scene.launch('Milestone', { bigThingId: id, replay: true });
      this.scene.bringToTop('Milestone');
    });
  }

  /**
   * Build the card → tiles map using Voronoi-style organic blotches:
   *
   *   1. Deterministic Fisher-Yates shuffle of all land tiles (so every
   *      player sees the same allocation).
   *   2. Take the first N tiles as seed points, where N = INVENTIONS.length.
   *   3. For each tile, find its nearest seed and assign it to that bucket.
   *
   * This naturally scales — add more cards, get more (smaller) blotches.
   * The blotches feel organic because seeds are drawn from a uniform random
   * shuffle of actual land tiles (no off-continent seeds), and Voronoi
   * regions trace gentle convex shapes.
   */
  private computeCardTileMap() {
    if (this.allTiles.length === 0) return;
    const N = INVENTIONS.length;

    // Seeded RNG so every player gets the same blotches. xorshift32 over
    // FNV-1a of a fixed seed string.
    let h = 2166136261;
    const seedStr = 'auscyclopedia-blotches-v1';
    for (let i = 0; i < seedStr.length; i++) {
      h ^= seedStr.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    const rand = () => {
      h ^= h << 13; h ^= h >>> 17; h ^= h << 5; h = h >>> 0;
      return h / 0xffffffff;
    };

    // Shuffle a copy so we can pick N random seeds without bias.
    const shuffled = [...this.allTiles];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    const seedCount = Math.min(N, shuffled.length);
    const seeds = shuffled.slice(0, seedCount).map((t) => ({ x: t.x, y: t.y }));

    // Bucket each tile to its nearest seed (squared distance is fine).
    const buckets: Phaser.GameObjects.Image[][] = Array.from(
      { length: seedCount }, () => []
    );
    for (const tile of this.allTiles) {
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < seedCount; i++) {
        const dx = tile.x - seeds[i].x;
        const dy = tile.y - seeds[i].y;
        const d = dx * dx + dy * dy;
        if (d < bestDist) { bestDist = d; best = i; }
      }
      buckets[best].push(tile);
    }

    INVENTIONS.forEach((inv, idx) => {
      this.cardTileMap.set(inv.id, buckets[idx] ?? []);
    });
  }

  /**
   * Map deliberately doesn't react to solves — it stays the unified red-dirt
   * skin throughout play. All progress signal lives in the HUD legend
   * (icons + count). The cardTileMap and the call sites remain so we can
   * re-enable per-solve feedback later if we change our mind.
   */
  private applyRevealedStatusToTiles(_animate: boolean, _onlyId?: string) {
    /* no-op */
  }

  private handleHover() {
    // Brighten the whole continent so it reads as "the entire map is clickable".
    for (const sprite of this.allTiles) sprite.setTint(0xfff5d6);
  }

  private clearHover() {
    for (const sprite of this.allTiles) sprite.clearTint();
  }
}
